// Case study pages live at "/case-studies/<id>" (routing is in App.js).
export const caseStudyPath = (id) => `/case-studies/${id}`;

export const caseStudies = [
    {
        id: 'fetch-and-fable',
        shortTitle: 'Fetch & Fable',
        title: 'Fetch & Fable: 0-to-1 AI Product Lifecycle & Funnel Optimization',
        summary: 'Scoping, launching, and scaling a custom generative-AI storybook store from initial spec to paying customers.',
        role: 'Software Engineer',
        period: 'May 2026 - Present',
        liveUrl: 'https://www.fetchandfable.co/',
        github: null,
        tech: ['Python', 'TypeScript', 'Claude', 'Gemini', 'Postgres', 'Docker', 'Stripe', 'Klaviyo'],
        sections: [
            {
                heading: 'Product Vision & Discovery',
                paragraphs: [
                    'Custom illustrated pet books traditionally require expensive manual commissions and weeks of turnaround time. Before building the full production pipeline for fetchandfable.co, I ran pre-launch product validation with dog owners to test willingness to pay and gather qualitative feedback on early AI-generated sample pages. That early user feedback surfaced a critical product requirement: pet owners immediately noticed if subtle markings (like ear flop, coat patches, or snout shape) shifted between pages, meaning character consistency across all 24 pages was a strict launch requirement.',
                ],
            },
            {
                heading: 'Product Strategy, GTM & Technical Execution',
                items: [
                    {
                        label: 'User-Validated Multi-Model Pipeline',
                        text: "Based on customer feedback during discovery, replaced single-prompt generation with a 3-stage pipeline: a vision model pass that extracts structured physical traits from the customer's dog photo, Claude to write the 24-page story, and Gemini to render all 24 illustrations with consistent character traits.",
                    },
                    {
                        label: 'Funnel Analytics & Affiliate Attribution',
                        text: 'Built end-to-end event tracking across the upload, preview, and Stripe checkout steps, along with custom affiliate promo codes and referral tracking to measure customer acquisition across partner launches.',
                    },
                    {
                        label: 'Lifecycle & Cart Recovery Flows',
                        text: 'Funnel tracking revealed user drop-off between photo upload and checkout completion. Integrated custom Klaviyo event webhooks to trigger personalized abandoned-cart and post-purchase email sequences based on where the user stopped in the funnel.',
                    },
                    {
                        label: 'Asynchronous Fulfillment Under Load',
                        text: 'Because affiliate partner posts created sudden traffic bursts while book generation took multiple minutes, synchronous processing risked dropped orders. Engineered a PostgreSQL job queue with concurrent workers, automated retries, crash recovery, and idempotent webhooks to fulfill paid orders reliably from checkout through third-party print fulfillment.',
                    },
                ],
            },
            {
                heading: 'Outcome & Business Impact',
                items: [
                    {
                        label: '0-to-1 Launch in Under 3 Months',
                        text: 'Delivered the web app from initial concept and customer validation to first paying customer in under three months (live since July 2026).',
                    },
                    {
                        label: '25% Revenue from Automated Retention',
                        text: 'Custom Klaviyo lifecycle and abandoned-cart flows now drive 25% of total attributed revenue.',
                    },
                    {
                        label: 'Zero Dropped Orders During Affiliate Spikes',
                        text: 'The asynchronous Postgres queue processed burst traffic from affiliate launches cleanly while post-purchase customer feedback guided ongoing prompt and illustration refinements.',
                    },
                ],
            },
        ],
    },
    {
        id: 'grizz-the-beagle',
        shortTitle: 'Grizz the Beagle',
        title: 'Grizz the Beagle: Conversational AI Support Agent & Unified Data Platform',
        summary: 'Stakeholder discovery that led to 80% support automation with chat categorization on Render, plus automated weekly LLM reporting across Shopify and Instagram/Meta.',
        role: 'Software Engineer (Contract)',
        period: 'May 2025 - May 2026',
        liveUrl: 'https://grizzthebeagle.com/',
        github: null,
        tech: ['Python', 'FastAPI', 'Claude', 'Postgres', 'Next.js', 'Shopify'],
        sections: [
            {
                heading: 'Stakeholder Discovery & Problem',
                paragraphs: [
                    'Before writing any code, I conducted stakeholder interviews with the business owner to uncover their biggest operational bottlenecks and customer blind spots. Answering repetitive customer inquiries took up hours of manual work each day (with 8+ hour response times), and the owner had no structured way to see what customers were asking about most. At the same time, storefront sales (Shopify), social and ad performance (Instagram and Meta Ads), and customer support interactions lived in separate silos. I translated those concerns into a two-part software roadmap: automating frontline support while capturing customer intent, and unifying cross-platform data into automated weekly insights.',
                ],
            },
            {
                heading: 'Product Strategy & Technical Execution',
                items: [
                    {
                        label: 'Production RAG Support Agent',
                        text: 'Scoped and shipped an embedded RAG customer support chatbot (FastAPI, Claude API) on the Shopify storefront, establishing strict retrieval guardrails for store policies, product sizing, and order lookups while escalating complex billing issues to human review.',
                    },
                    {
                        label: 'Conversation Taxonomy & Tracking on Render',
                        text: 'Deployed the backend service on Render and built an automated classification pipeline that logged and categorized every chat into structured buckets (Sizing, Product Specs, Policy, Billing, and Orders). This turned raw support logs into actionable product analytics showing exactly where shoppers experienced friction.',
                    },
                    {
                        label: 'Cross-Platform Data Warehouse & Next.js Dashboard',
                        text: 'Built a nightly ETL pipeline into PostgreSQL that unified data from Shopify, Instagram/Meta Ads, and the Render chatbot service, surfacing cross-platform metrics like blended ROAS, ROI, and inquiry trends in a custom Next.js dashboard.',
                    },
                    {
                        label: 'Automated Weekly LLM Executive Reports',
                        text: 'Built an automated weekly reporting workflow using an LLM to synthesize Shopify sales, Instagram/Meta campaign performance, and categorized chat trends into plain-English executive summaries highlighting top-selling products, best-performing ad campaigns, and overall sales health.',
                    },
                ],
            },
            {
                heading: 'Outcome & Business Impact',
                items: [
                    {
                        label: '80% Support Automation & Sub-10s Responses',
                        text: 'Automated 80% of incoming customer inquiries and cut average response time from 8+ hours to under 10 seconds.',
                    },
                    {
                        label: 'Actionable Customer & Product Intelligence',
                        text: 'Categorized chat analytics (Sizing, Product Specs, Policy, Billing, Orders) gave the owner direct visibility into customer questions and informed storefront copy improvements.',
                    },
                    {
                        label: 'Zero Manual Reporting',
                        text: 'Replaced hours of manual weekly spreadsheet work with the unified Next.js dashboard and automated weekly LLM executive summaries covering product sales and ad campaign performance.',
                    },
                ],
            },
        ],
    },
    {
        id: 'sabergraphs',
        shortTitle: 'SaberGraphs',
        title: 'SaberGraphs: Natural Language Analytics Interface for Non-Technical Users',
        summary: 'Replacing dropdown-heavy baseball analytics UIs with an AI agent equipped with SQL, data-analyst scripts, and ML prediction tools, plus CSV and chart exports.',
        role: 'Personal project',
        period: '2026',
        liveUrl: 'https://sabergraphs.com',
        github: 'https://github.com/cmartinez131/sabergraphs',
        tech: ['Python', 'JavaScript', 'React', 'FastAPI', 'PostgreSQL', 'Claude API', 'Docker'],
        sections: [
            {
                heading: 'Product Vision & Problem',
                paragraphs: [
                    'Exploring advanced MLB Statcast data (3.8M+ pitches and 15,000+ player-seasons) currently forces users into an awkward trade-off: either write complex SQL from scratch, or navigate existing public tools like Baseball Savant that bury insights behind dozens of rigid dropdown menus and filter combinations. Non-technical fans, analysts, and writers need a faster way to ask questions, run predictive projections, and export shareable visuals without fighting the UI.',
                ],
            },
            {
                heading: 'Product Strategy & Spec Definition',
                items: [
                    {
                        label: 'Conversational UI over Dropdown Fatigue',
                        text: 'Designed SaberGraphs to replace multi-step dropdown filtering with a natural-language interface that translates plain-English baseball questions into SQL queries and interactive React charts.',
                    },
                    {
                        label: 'Agentic Tool-Use Architecture',
                        text: 'Rather than relying solely on open-ended LLM text generation, equipped the AI agent with specialized, deterministic tools: a natural-language SQL query tool, pre-written data-analyst scripts that reliably shape complex sabermetric outputs, and a machine learning prediction tool so the agent can generate player projections alongside historical lookups.',
                    },
                    {
                        label: 'Data Modeling & Quality Acceptance Criteria',
                        text: 'Normalized raw Statcast feeds (3.8M+ pitches and 15,000+ player-seasons) into indexed season-level PostgreSQL tables with 100+ automated data quality checks and crash-safe chunk ingestion so queries return in seconds.',
                    },
                    {
                        label: 'Prompt Guardrails & Hallucination Mitigation',
                        text: 'Configured the Claude API layer with strict schema context and tool-selection rules so ambiguous user prompts map cleanly to verified sabermetric definitions and pre-tested analytical scripts.',
                    },
                ],
            },
            {
                heading: 'Outcome, Workflow & Roadmap',
                items: [
                    {
                        label: 'End-to-End Analyst Workflow',
                        text: 'Delivered a self-serve analytics web app where users can go from a plain-English question to an interactive visualization in seconds, with built-in support for exporting query results as CSV files or downloadable chart images for reports and social sharing.',
                    },
                    {
                        label: 'Next-Phase Roadmap (MCP-Style Tool Expansion)',
                        text: "Architected the backend tool router to be modular, with the ongoing product roadmap focused on expanding the agent's library of deterministic analytical and predictive tools (similar to an MCP server architecture) for multi-step research workflows.",
                    },
                ],
            },
        ],
    },
];

export const getCaseStudy = (id) => caseStudies.find((cs) => cs.id === id) || null;
